import {Component,OnInit,ViewChild} from "@angular/core";
import {ActivatedRoute,Router} from "@angular/router";
import {BehaviorSubject} from "rxjs";
import {filter,finalize,first} from "rxjs/operators";
import {NgForm} from "@angular/forms";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {ListView} from "@domain/common/list-view";
import {Action} from "@domain/common/list-view/action";
import {Result,TypeCodeErreur} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {IndemnitesRegleAttributionPrestationAddComponent} from "@components/admin/frais/indemnites/indemnites-regles-attribution/detail/prestation-add/indemnites-regle-attribution-prestation-add.component";
import {AutocompleteSearchComponent} from "@share/component/autocomplete/autocomplete-search.component";
import {FilterValue} from "@domain/common/list-view";
import {PrestationService} from "@services/admin/frais/prestation.service";

/**
 * Détail d'une règle d'attribution
 */
@Component({
    host: {'data-test-id': 'indemnites-regle-attribution-detail'},
    selector: 'indemnites-regle-attribution-detail',
    templateUrl: './indemnites-regle-attribution-detail.component.html'
})
export class IndemnitesRegleAttributionDetailComponent implements OnInit {
    /** Formulaire */
    @ViewChild('form') form: NgForm;

    /** Règle éditée */
    regleAttribution: RegleAttributionDetail;

    /** Prestations associées à la règle */
    listeReglesPrestas: RegleAttributionPresta[] = [];

    /** Mode création */
    isCreation: boolean;

    /** Enregistrement en cours */
    isSaving: boolean = false;

    /** Chargement en cours */
    isLoading: boolean = true;

    /** Liste des différents onglets */
    listeTabItems: Array<PageHeaderItem> = [{
        code: Onglets.GENERALITES,
        libelle: this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.onglets.generalites')
    }];

    /** Onglet courant */
    selectedItem: PageHeaderItem = this.listeTabItems[0];

    /** Liste des actions du bouton flottant */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Configuration d'entête standard pour la liste des prestations */
    listeViewEntetePrestations: ListView<any, any>;

    /** Référence vers l'enum des onglets pour l'utiliser dans le template */
    Onglets = Onglets;

    /**
     * Constructeur
     */
    constructor(private route: ActivatedRoute,
                private router: Router,
                private http: HttpClient,
                private translateService: TranslateService,
                private toastrService: ToastrService,
                private confirmService: ConfirmService,
                private prestationService: PrestationService,
                private matDialog: MatDialog) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        const idRegleAttribution = Number(this.route.snapshot.paramMap.get('idRegleAttribution'));

        //Initialisation du mode
        this.isCreation = idRegleAttribution === 0;

        //Initialisation des actions
        this.defineListeActions();

        //Initialisation de l'entête prestations via le composant partagé list-view-search
        this.initEntetePrestations();

        //Chargement des données
        this.loadRegleAttribution(idRegleAttribution);
    }

    /**
     * Initialisation de l'entête prestations au format list-view standard
     */
    private initEntetePrestations(): void {
        const listeActionsEntete: Action[] = [{
            icon: 'add',
            onPress: () => this.onAddPrestation(),
            tooltip: this.translateService.instant('global.actions.ajouter')
        }];

        this.listeViewEntetePrestations = new ListView<any, any>({
            //Le constructeur impose un composant, même pour un usage entête-only
            component: null as any,
            title: this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.title'),
            isSimple: true,
            showPagination: false,
            listeActions: listeActionsEntete
        });
    }

    /**
     * Retour du titre de page
     */
    getTitle(): string {
        return this.isCreation
            ? this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.titleCreation')
            : this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.titleModification');
    }

    /**
     * Changement d'onglet
     */
    onSelectedItemChange(selectedItem: PageHeaderItem): void {
        this.selectedItem = selectedItem;
    }

    /**
     * Retour vers la liste
     */
    onGoBack(): void {
        void this.router.navigate(['Admin/Frais/Indemnites/ReglesAttribution']);
    }

    /**
     * Définition des actions du bouton flottant
     */
    private defineListeActions(): void {
        const liste: FloatingButtonAction[] = [{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.enregistrer',
            doAction: () => this.saveRegleAttribution(),
            isDisabled: () => !(this.form?.valid ?? true) || !this.isRegleMetierValid()
        }];

        if (!this.isCreation) {
            liste.push({
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                doAction: () => this.deleteRegleAttribution()
            });
        }

        this.listeActions.next(liste);
    }

    /**
     * Chargement de la règle à partir de l'identifiant
     */
    private loadRegleAttribution(idRegleAttribution: number): void {
        this.isLoading = true;

        this.http.post<Result<any>>(`${environment.baseUrl}/controller/RegleAttribution/loadRegleAttribution/${idRegleAttribution}`,{})
            .subscribe({
                next: result => {
                    if (result?.codeErreur === TypeCodeErreur.NO_ERROR && result?.data?.regle) {
                        this.regleAttribution = new RegleAttributionDetail(result.data.regle);
                        this.joursUsageToDispBooleans(this.regleAttribution);
                        this.normalizeCalendaireFlags(this.regleAttribution);
                        this.normalizeModeGestionFields(this.regleAttribution);
                        this.normalizeHoraireToInput(this.regleAttribution);

                        //Chargement des prestations uniquement en mode édition
                        this.loadReglesPrestations(idRegleAttribution);
                    } else if (this.isCreation) {
                        this.regleAttribution = this.creerRegleAttributionParDefaut();
                    } else {
                        TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                        this.onGoBack();
                    }

                    this.isLoading = false;
                },
                error: () => {
                    if (this.isCreation) {
                        this.regleAttribution = this.creerRegleAttributionParDefaut();
                        this.isLoading = false;
                        return;
                    }

                    this.toastrService.error(this.translateService.instant('global.errors.chargement'));
                    this.onGoBack();
                }
            });
    }

    /**
     * Chargement des prestations associées à une règle existante
     */
    private loadReglesPrestations(idRegleAttribution: number): void {
        if (!idRegleAttribution) {
            this.listeReglesPrestas = [];
            return;
        }

        this.http.post<Result<any>>(`${environment.baseUrl}/controller/Prestation/listeReglesPrestationsByRegleId/${idRegleAttribution}`,{})
            .subscribe({
                next: result => {
                    this.listeReglesPrestas = (result?.data?.listeReglesPrestas ?? []) as RegleAttributionPresta[];

                    //Alerte utilisateur si la règle contient plusieurs domaines, comme en V9
                    if (!this.isDomainePrestasUnique()) {
                        this.toastrService.error(this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.domainesDifferents'));
                    }
                },
                error: () => {
                    this.listeReglesPrestas = [];
                }
            });
    }

    /**
     * Retourne le libellé de type de prestation
     */
    getTypePrestationLabel(reglePresta: RegleAttributionPresta): string {
        const typePrestation = reglePresta?.prestation?.type;

        if (typePrestation === TypePrestation.REEL) {
            return this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.types.reel');
        }

        if (typePrestation === TypePrestation.PLAFOND) {
            return this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.types.plafond');
        }

        if (typePrestation === TypePrestation.FORFAIT) {
            return this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.types.forfait');
        }

        return '-';
    }

    /**
     * Retourne le libellé actif/inactif d'une prestation
     */
    getActifLabel(reglePresta: RegleAttributionPresta): string {
        return reglePresta?.prestation?.actif
            ? this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.oui')
            : this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.non');
    }

    /**
     * Retourne les initiales de la prestation pour l'avatar
     */
    getInitialesPrestation(reglePresta: RegleAttributionPresta): string {
        const libelle = (reglePresta?.prestation?.libelle ?? '').trim();
        if (!libelle) {
            return 'PR';
        }

        const mots = libelle.split(/\s+/).filter((mot: string) => !!mot);
        if (mots.length === 1) {
            return mots[0].substring(0,2).toUpperCase();
        }

        return `${mots[0].charAt(0)}${mots[1].charAt(0)}`.toUpperCase();
    }

    /**
     * Action d'ajout d'une prestation : ouvre directement la modale de recherche de prestations
     * sans passer par la popup intermédiaire dont les champs étaient en lecture seule.
     */
    onAddPrestation(): void {
        //Sécurisation : l'association n'est possible qu'en mode édition d'une règle enregistrée
        if (!this.regleAttribution?.id) {
            return;
        }

        //Récupération du domaine imposé par les prestations déjà associées
        const idDomainePrestation = this.getIdDomainePrestationLie();

        //Chargement des domaines de prestation pour alimenter le filtre select
        this.prestationService.listeDomainePrestation()
            .pipe(first())
            .subscribe((domaines: any[]) => {
                const listeDomaines: FilterValue[] = (domaines || []).map(domaine => ({
                    code: String(domaine.idReferentiel || domaine.id),
                    libelle: domaine.libelle
                }));

                //Ouverture directe de la modale de recherche partagée (équivalent au clic sur la loupe d'un autocomplete)
                this.matDialog.open(AutocompleteSearchComponent,{
                    minWidth: 720,
                    data: {
                        title: this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.popup.title'),
                        options: IndemnitesRegleAttributionPrestationAddComponent.buildAutocompleteOptions(this.translateService,listeDomaines),
                        filter: idDomainePrestation != null ? {idDomainePrestation} : {}
                    }
                }).afterClosed().subscribe((prestation: any) => {
                    //Si fermeture sans sélection, on annule
                    if (!prestation) {
                        return;
                    }

                    //Contrôle de cohérence : une règle ne peut contenir qu'un seul domaine de prestation
                    if (this.isDomaineDifferentFromList(prestation)) {
                        this.toastrService.error(this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.domainesDifferents'));
                        return;
                    }

                    //Construction du lien règle / prestation en cohérence avec le legacy V9
                    const reglePresta = {
                        id: 0,
                        bonus: null,
                        qte: 1,
                        prestation: prestation,
                        regleAttribution: this.regleAttribution
                    };

                    //Enregistrement de l'association
                    this.http.put<Result<any>>(`${environment.baseUrl}/controller/Prestation/saveLienReglePresta`,reglePresta)
                        .subscribe(result => {
                            if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                                this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                                this.loadReglesPrestations(this.regleAttribution.id);
                            } else {
                                TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                            }
                        });
                });
            });
    }

    /**
     * Vérifie si la prestation à ajouter a un domaine différent de celles déjà liées à la règle
     */
    private isDomaineDifferentFromList(prestation: any): boolean {
        const idDomainePrestation = prestation?.idDomainePrestation;

        //Sans domaine identifié, pas de blocage côté front
        if (idDomainePrestation === null || idDomainePrestation === undefined) {
            return false;
        }

        //Une liste vide n'impose aucune contrainte de domaine
        if (!this.listeReglesPrestas?.length) {
            return false;
        }

        //Si au moins une prestation existante a un domaine différent, l'ajout est refusé
        return this.listeReglesPrestas
            .some((reglePresta: RegleAttributionPresta) => reglePresta?.prestation?.idDomainePrestation !== idDomainePrestation);
    }

    /**
     * Retourne l'identifiant du domaine de prestation commun aux prestations déjà liées, s'il existe
     */
    private getIdDomainePrestationLie(): number | null {
        if (!this.listeReglesPrestas?.length) {
            return null;
        }

        const idDomainePrestation = this.listeReglesPrestas[0]?.prestation?.idDomainePrestation;

        //Validation que toutes les prestations existantes partagent le même domaine
        if (idDomainePrestation == null || this.listeReglesPrestas
            .some((reglePresta: RegleAttributionPresta) => reglePresta?.prestation?.idDomainePrestation !== idDomainePrestation)) {
            return null;
        }

        return idDomainePrestation;
    }

    /**
     * Suppression d'une prestation associée
     */
    onDeletePrestation(reglePresta: RegleAttributionPresta): void {
        const idLienPrestaRegle = reglePresta?.id;
        if (!idLienPrestaRegle || !this.regleAttribution?.id) {
            return;
        }

        this.confirmService.showConfirm(this.translateService.instant('admin.frais.indemnites.reglesAttribution.detail.prestations.confirmationSuppression'))
            .pipe(filter((isConfirmed: boolean) => isConfirmed))
            .subscribe(() => {
                this.http.delete<Result>(`${environment.baseUrl}/controller/Prestation/deletePrestaRegle/${idLienPrestaRegle}`)
                    .subscribe(result => {
                        if (result?.codeErreur === TypeCodeErreur.NO_ERROR || !result?.codeErreur) {
                            this.toastrService.success(this.translateService.instant('global.success.suppression'));
                            this.loadReglesPrestations(this.regleAttribution.id);
                        } else {
                            TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                        }
                    });
            });
    }

    /**
     * Vérifie qu'une règle n'est liée qu'à un seul domaine de prestation
     */
    private isDomainePrestasUnique(): boolean {
        const listeDomaines: number[] = [];

        for (const reglePresta of this.listeReglesPrestas) {
            const idDomaine = reglePresta?.prestation?.idDomainePrestation;
            if (idDomaine === null || idDomaine === undefined) {
                continue;
            }

            if (!listeDomaines.includes(idDomaine)) {
                listeDomaines.push(idDomaine);
            }
        }

        return listeDomaines.length <= 1;
    }

    /**
     * Création d'une règle par défaut
     */
    private creerRegleAttributionParDefaut(): RegleAttributionDetail {
        return new RegleAttributionDetail({
            id: 0,
            libelle: null,
            actif: true,
            alimentationNDF: true,
            couverturePartielle: false,
            joursUsage: '11111111',
            dureeMin: 0,
            dureeMax: 24,
            dispLu: true,
            dispMa: true,
            dispMe: true,
            dispJe: true,
            dispVe: true,
            dispSa: true,
            dispDi: true,
            dispJF: true,
            applicablePremJour: false,
            applicableJourInterm: false,
            applicableDernJour: false,
            aucunExcedantCalendaire: false,
            excedantCalendaire: false,
            toutesMissions: false,
            margeDeb: 0,
            margeFin: 0
        });
    }

    /**
     * Enregistrement de la règle
     */
    private saveRegleAttribution(): void {
        this.isSaving = true;

        const payload = new RegleAttributionDetail(this.regleAttribution);

        //Garantie du booléen obligatoire attendu par le backend legacy
        payload.alimentationNDF = !!payload.alimentationNDF;

        //Normalisation de cohérence des champs selon le mode/calendaire
        this.normalizeCalendaireFlags(payload);
        this.normalizeModeGestionFields(payload);

        //Synchronisation des disponibilités vers la chaîne legacy
        this.dispBooleansToJoursUsage(payload);

        //Conversion des horaires UI vers le format legacy attendu par le backend
        this.normalizeHoraireToLegacy(payload);

        this.http.put<Result<any>>(
            `${environment.baseUrl}/controller/RegleAttribution/saveRegleAttribution`,
            JSON.stringify(payload),
            {headers: new HttpHeaders({'Content-Type': 'text/plain; charset=utf-8'})}
        )
            .pipe(finalize(() => this.isSaving = false))
            .subscribe(result => {
                if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

                    //Après une création, passage immédiat en édition pour permettre l'association de prestations
                    const idRegleCreee = result?.data?.regle?.id;
                    if (this.isCreation && idRegleCreee) {
                        this.regleAttribution.id = idRegleCreee;
                        this.isCreation = false;
                        this.defineListeActions();
                        void this.router.navigate([`Admin/Frais/Indemnites/ReglesAttribution/${idRegleCreee}`],{replaceUrl: true});
                    }
                } else {
                    TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                }
            });
    }

    /**
     * Suppression de la règle
     */
    private deleteRegleAttribution(): void {
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter((isConfirmed: boolean) => isConfirmed))
            .subscribe(() => {
                this.http.delete<Result>(`${environment.baseUrl}/controller/RegleAttribution/deleteRegleAttribution/${this.regleAttribution?.id}`)
                    .subscribe(result => {
                        if (result?.codeErreur === TypeCodeErreur.NO_ERROR) {
                            this.toastrService.success(this.translateService.instant('global.success.suppression'));
                            this.onGoBack();
                        } else {
                            TypeCodeErreur.showError(result?.codeErreur,this.translateService,this.toastrService);
                        }
                    });
            });
    }

    /**
     * Initialise les booléens de disponibilité depuis joursUsage
     */
    private joursUsageToDispBooleans(regle: RegleAttributionDetail): void {
        if (!regle?.joursUsage || regle.joursUsage.length !== 8) {
            return;
        }

        regle.dispLu = regle.joursUsage.charAt(0) === '1';
        regle.dispMa = regle.joursUsage.charAt(1) === '1';
        regle.dispMe = regle.joursUsage.charAt(2) === '1';
        regle.dispJe = regle.joursUsage.charAt(3) === '1';
        regle.dispVe = regle.joursUsage.charAt(4) === '1';
        regle.dispSa = regle.joursUsage.charAt(5) === '1';
        regle.dispDi = regle.joursUsage.charAt(6) === '1';
        regle.dispJF = regle.joursUsage.charAt(7) === '1';
    }

    /**
     * Actualise joursUsage depuis les booléens de disponibilité
     */
    private dispBooleansToJoursUsage(regle: RegleAttributionDetail): void {
        regle.joursUsage = '';
        regle.joursUsage = (regle.dispJF ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispDi ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispSa ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispVe ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispJe ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispMe ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispMa ? '1' : '0') + regle.joursUsage;
        regle.joursUsage = (regle.dispLu ? '1' : '0') + regle.joursUsage;
    }

    /**
     * Normalise les champs dépendants du mode de gestion
     */
    private normalizeModeGestionFields(regle: RegleAttributionDetail): void {
        if (!regle) {
            return;
        }

        if (regle.modeGestion === TypeModeGestion.TRANCHE_HORAIRE) {
            regle.dureeMin = 0;
            regle.dureeMax = 24;
            return;
        }

        if (regle.modeGestion === TypeModeGestion.TEMPS_PASSE) {
            regle.heureDeb = null;
            regle.heureFin = null;
            regle.margeDeb = 0;
            regle.margeFin = 0;
            regle.dureeMin = Number(regle.dureeMin);
            regle.dureeMax = Number(regle.dureeMax);

            //Sécurisation cohérence min/max
            if (regle.dureeMin >= regle.dureeMax) {
                regle.dureeMax = Math.min(24,regle.dureeMin + 1);
            }
            return;
        }

        //UNE_FOIS_PAR_JOUR
        regle.heureDeb = null;
        regle.heureFin = null;
        regle.margeDeb = 0;
        regle.margeFin = 0;
        regle.dureeMin = 0;
        regle.dureeMax = 24;
    }

    /**
     * Normalise les cases calendaires et applicables
     */
    private normalizeCalendaireFlags(regle: RegleAttributionDetail): void {
        if (!regle) {
            return;
        }

        regle.aucunExcedantCalendaire = !!regle.aucunExcedantCalendaire;
        regle.excedantCalendaire = !!regle.excedantCalendaire;

        //Equivalent "Toutes les missions"
        regle.toutesMissions = regle.aucunExcedantCalendaire && regle.excedantCalendaire;

        //Au moins une des deux cases doit être cochée
        if (!regle.aucunExcedantCalendaire && !regle.excedantCalendaire) {
            regle.aucunExcedantCalendaire = true;
            regle.toutesMissions = false;
        }

        //Si uniquement mission <= 1 jour calendaire, les jours intermédiaire/dernier sont sans objet
        //Le premier jour n'est pas forcé ici : il est coché par onChangeCalendaire mais reste décochable (comportement V9)
        if (regle.aucunExcedantCalendaire && !regle.excedantCalendaire) {
            regle.applicableJourInterm = false;
            regle.applicableDernJour = false;
        }
    }

    /**
     * Normalise une durée entre 0 et 24
     */
    private normalizeDureeValue(value: number,defaultValue: number = 0): number {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return defaultValue;
        }

        return Number(value);
    }

    /**
     * Convertit les horaires legacy (HHHMM) vers le format input time (HH:mm)
     */
    private normalizeHoraireToInput(regle: RegleAttributionDetail): void {
        if (!regle) {
            return;
        }

        regle.heureDeb = this.toInputTime(regle.heureDeb);
        regle.heureFin = this.toInputTime(regle.heureFin);
    }

    /**
     * Convertit les horaires input time (HH:mm) vers le format legacy (HHHMM)
     */
    private normalizeHoraireToLegacy(regle: RegleAttributionDetail): void {
        if (!regle) {
            return;
        }

        regle.heureDeb = this.toLegacyTime(regle.heureDeb);
        regle.heureFin = this.toLegacyTime(regle.heureFin);
    }

    /**
     * Convertit une heure vers le format HH:mm pour un input type time
     */
    private toInputTime(valeur: string): string {
        if (!valeur) {
            return '';
        }

        //Format déjà compatible input
        if (/^\d{2}:\d{2}$/.test(valeur)) {
            return valeur;
        }

        //Format legacy HHHMM
        if (/^\d{2}H\d{2}$/.test(valeur)) {
            return valeur.replace('H',':');
        }

        return '';
    }

    /**
     * Convertit une heure vers le format legacy HHHMM pour le backend
     */
    private toLegacyTime(valeur: string): string {
        if (!valeur) {
            return '00H00';
        }

        //Format déjà legacy
        if (/^\d{2}H\d{2}$/.test(valeur)) {
            return valeur;
        }

        //Format input HTML time
        if (/^\d{2}:\d{2}$/.test(valeur)) {
            return valeur.replace(':','H');
        }

        return '00H00';
    }

    /**
     * Indique si les règles métier V9 permettent la sauvegarde
     */
    private isRegleMetierValid(): boolean {
        if (!this.regleAttribution?.aucunExcedantCalendaire && !this.regleAttribution?.excedantCalendaire) {
            return false;
        }

        if (this.regleAttribution.modeGestion === TypeModeGestion.TRANCHE_HORAIRE) {
            return !!this.regleAttribution.heureDeb
                && !!this.regleAttribution.heureFin
                && this.regleAttribution.heureDeb < this.regleAttribution.heureFin;
        }

        if (this.regleAttribution.modeGestion === TypeModeGestion.TEMPS_PASSE) {
            const dureeMin = Number(this.regleAttribution.dureeMin);
            const dureeMax = Number(this.regleAttribution.dureeMax);
            const dureesRenseignees = !!this.regleAttribution.dureeMin && !!this.regleAttribution.dureeMax;

            return dureeMin >= 0 && dureeMin <= 24
                && dureeMax >= 0 && dureeMax <= 24
                && (!dureesRenseignees || dureeMin < dureeMax);
        }

        return true;
    }
}

/**
 * Onglets du détail règle d'attribution
 */
export enum Onglets {
    GENERALITES = 'Généralités'
}

/**
 * Modes de gestion de la règle d'attribution
 */
export enum TypeModeGestion {
    UNE_FOIS_PAR_JOUR = 'UNE_FOIS_PAR_JOUR',
    TRANCHE_HORAIRE = 'TRANCHE_HORAIRE',
    TEMPS_PASSE = 'TEMPS_PASSE'
}

/**
 * Types de prestation
 */
export enum TypePrestation {
    REEL = 0,
    PLAFOND = 1,
    FORFAIT = 2
}

/**
 * Prestation liée à une règle d'attribution
 */
export class RegleAttributionPresta {
    /** Identifiant du lien règle/prestation */
    id: number;

    /** Quantité associée */
    qte: number;

    /** Informations de prestation */
    prestation: PrestationRegleAttribution;
}

/**
 * Données simplifiées d'une prestation liée à la règle
 */
export class PrestationRegleAttribution {
    /** Libellé de la prestation */
    libelle: string;

    /** Domaine de prestation */
    domainePrestation: string;

    /** Identifiant du domaine de prestation */
    idDomainePrestation: number;

    /** Type de remboursement de la prestation */
    type: TypePrestation;

    /** Statut actif de la prestation */
    actif: boolean;
}

/**
 * Données du détail règle d'attribution
 */
export class RegleAttributionDetail {
    /** Identifiant */
    id: number;

    /** Libellé */
    libelle: string;

    /** Mode de gestion */
    modeGestion: TypeModeGestion;

    /** Règle active */
    actif: boolean;

    /** Alimentation des notes de frais */
    alimentationNDF: boolean;

    /** Heure de début */
    heureDeb: string;

    /** Heure de fin */
    heureFin: string;

    /** Couverture partielle de la tranche horaire */
    couverturePartielle: boolean;

    /** Durée minimale */
    dureeMin: number;

    /** Durée maximale */
    dureeMax: number;

    /** Marge avant */
    margeDeb: number;

    /** Marge après */
    margeFin: number;

    /** Applicabilité premier jour */
    applicablePremJour: boolean;

    /** Applicabilité jours intermédiaires */
    applicableJourInterm: boolean;

    /** Applicabilité dernier jour */
    applicableDernJour: boolean;

    /** Mission n'excédant pas une journée calendaire */
    aucunExcedantCalendaire: boolean;

    /** Mission excédant une journée calendaire */
    excedantCalendaire: boolean;

    /** Toutes les missions */
    toutesMissions: boolean;

    /** Chaîne des disponibilités par jour */
    joursUsage: string;

    /** Disponibilité lundi */
    dispLu: boolean;

    /** Disponibilité mardi */
    dispMa: boolean;

    /** Disponibilité mercredi */
    dispMe: boolean;

    /** Disponibilité jeudi */
    dispJe: boolean;

    /** Disponibilité vendredi */
    dispVe: boolean;

    /** Disponibilité samedi */
    dispSa: boolean;

    /** Disponibilité dimanche */
    dispDi: boolean;

    /** Disponibilité jours fériés */
    dispJF: boolean;

    /**
     * Constructeur
     */
    constructor(data?: Partial<RegleAttributionDetail>) {
        Object.assign(this,data);
    }
}
